import { useSelector } from 'react-redux'
import { RootState } from '@/utils/store/store'
import { defaultSkeletonGroups } from './sideNavSkeletonItems'
import type { SideNavSkeletonGroup, SideNavSkeletonItem } from './sideNavSkeletonItems'
import './SkeletonLoading.scss'

interface SideNavigationSkeletonProps {
    isMenuOpen: boolean
    toggleMenu: (open?: boolean) => void
    /** The lists and items to stand for (the menu of the page); the default menu when nothing is known */
    groups?: SideNavSkeletonGroup[]
}

// The width of the text of a label per character, as measured on the real label (Bosch Sans at 1rem)
const LABEL_EM_PER_CHARACTER = 0.54

const SkeletonItem = ({ item, isMenuOpen }: { item: SideNavSkeletonItem; isMenuOpen: boolean }) => (
    <li className='a-menu-item' role='none'>
        <div className='a-menu-item__wrapper'>
            <button type='button' className='a-menu-item__link side-nav-skeleton__link' tabIndex={-1}>
                <span className='side-nav-skeleton__icon' />
                {isMenuOpen && (
                    <>
                        <span
                            className='side-nav-skeleton__label'
                            style={{ width: `${Math.max(3, item.labelLength) * LABEL_EM_PER_CHARACTER}em` }}
                        />
                        {item.hasBadge && <span className='side-nav-skeleton__badge' />}
                    </>
                )}
            </button>
        </div>
    </li>
)

const SideNavigationSkeleton = ({ isMenuOpen, toggleMenu, groups = defaultSkeletonGroups }: SideNavigationSkeletonProps) => {
    const { config } = useSelector((state: RootState) => state.application)

    return (
        <div className='mma-skeleton-loader side-navigation'>
            <nav
                className={`m-side-navigation -contrast ${isMenuOpen ? '-open' : ''}`}
                aria-label='Side Navigation'
                aria-hidden='true'
            >
                <div className='m-side-navigation__header'>
                    <div className='m-side-navigation__header__label -size-l highlight'>
                        {config?.name}
                    </div>
                    <button
                        type='button'
                        className='a-button a-button--integrated -without-label m-side-navigation__header__trigger -open'
                        aria-haspopup='false'
                        aria-label='Open Side Navigation'
                        tabIndex={0}
                        onClick={() => toggleMenu(true)}
                    >
                        <i className='a-icon a-button__icon boschicon-bosch-ic-list-view-mobile' />
                    </button>
                    <button
                        type='button'
                        className='a-button a-button--integrated -without-label m-side-navigation__header__trigger -close'
                        aria-haspopup='false'
                        aria-label='Close Side Navigation'
                        tabIndex={-1}
                        onClick={() => toggleMenu(false)}
                    >
                        <i className='a-icon a-button__icon boschicon-bosch-ic-close' />
                    </button>
                </div>

                {/* The same lists as the real navigation: a list that takes the free height, and below it the group
                    with a border (`navigation-group`, see Group.tsx) */}
                {groups.map((group, groupIndex) => (
                    <ul key={groupIndex} className={`m-menu-group ${group.customClasses}`} role='menubar' aria-orientation='vertical'>
                        {group.nested ? (
                            <li className='navigation-group a-menu-item -open' role='none'>
                                <div className='head a-menu-item__wrapper' />
                                <ul className='m-menu-group__group' role='menu'>
                                    {group.items.map((item, itemIndex) => (
                                        <SkeletonItem key={itemIndex} item={item} isMenuOpen={isMenuOpen} />
                                    ))}
                                </ul>
                            </li>
                        ) : (
                            group.items.map((item, itemIndex) => (
                                <SkeletonItem key={itemIndex} item={item} isMenuOpen={isMenuOpen} />
                            ))
                        )}
                    </ul>
                ))}
            </nav>
        </div>
    )
}

export default SideNavigationSkeleton
